import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import AiFeatureToolPrep from "../../shared/Popup/AiFeatureToolPrep";
import { aiBackgroundSingle } from "../../../../../services/api/tools/studio_features";
import AI_BACKGROUND_TEMPLATES from "../../../../../config/tools/aiBackgroundTemplates.json";
import "./BackgroundImageToolPrep.css";

/** Matches pricing catalog: Ai background images = 3 credits. */
const GENERATION_CREDITS = 3;
const PROMPT_MAX_LENGTH = 1000;
/** Generate always uses webp; download format is chosen on Download Options. */
const DEFAULT_OUTPUT_FORMAT = "webp";

const ACCEPT = "image/jpeg,image/png,image/webp";

const ChevronIcon = () => (
  <svg
    className="abitp-composer__chevron"
    viewBox="0 0 12 12"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M2.5 4.5L6 8L9.5 4.5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CreditsIcon = () => (
  <svg
    className="abitp-done-credits__icon"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <ellipse
      cx="8"
      cy="4.5"
      rx="5"
      ry="2.2"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path
      d="M3 4.5v3c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2v-3"
      stroke="currentColor"
      strokeWidth="1.3"
    />
    <path
      d="M3 7.5v3c0 1.2 2.2 2.2 5 2.2s5-1 5-2.2v-3"
      stroke="currentColor"
      strokeWidth="1.3"
    />
  </svg>
);

const PlusIcon = () => (
  <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 4v12M4 10h12"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
    />
  </svg>
);

/**
 * Positions a portaled menu full prompt-box width, 5px above the trigger chip.
 * @param {boolean} open
 * @param {React.RefObject<HTMLElement>} composerRef — width + horizontal align
 * @param {React.RefObject<HTMLElement>} anchorRef — vertical anchor (chip)
 */
function useComposerMenuPosition(open, composerRef, anchorRef) {
  const [style, setStyle] = useState(null);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return undefined;
    }

    const update = () => {
      const box = composerRef.current?.getBoundingClientRect();
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!box || !anchor) return;
      const gap = 5;
      const maxHeight = Math.max(160, Math.min(360, anchor.top - gap - 8));
      setStyle({
        position: "fixed",
        left: Math.round(box.left),
        width: Math.round(box.width),
        bottom: Math.round(window.innerHeight - anchor.top + gap),
        maxHeight: Math.round(maxHeight),
        zIndex: 4200,
      });
    };

    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [open, composerRef, anchorRef]);

  return style;
}

function BackgroundImageControls({
  busy,
  sourceImageUrl,
  prompt,
  setPrompt,
  guidanceFile,
  setGuidanceFile,
}) {
  const [guidancePreview, setGuidancePreview] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [composerActive, setComposerActive] = useState(false);

  const guidanceInputRef = useRef(null);
  const guidancePreviewRef = useRef(null);
  const composerRef = useRef(null);
  const templatesChipRef = useRef(null);
  const menusRef = useRef(null);
  guidancePreviewRef.current = guidancePreview;

  const controlsDisabled = busy || !sourceImageUrl;
  const activeTemplate = AI_BACKGROUND_TEMPLATES.find(
    (t) => t.prompt === prompt.trim()
  );

  const templatesStyle = useComposerMenuPosition(
    templatesOpen,
    composerRef,
    templatesChipRef
  );

  useEffect(() => {
    return () => {
      if (guidancePreviewRef.current) {
        URL.revokeObjectURL(guidancePreviewRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (sourceImageUrl) return;
    if (guidancePreviewRef.current) {
      URL.revokeObjectURL(guidancePreviewRef.current);
    }
    setGuidancePreview(null);
    setGuidanceFile(null);
    setPrompt("");
    setTemplatesOpen(false);
    setComposerActive(false);
  }, [sourceImageUrl, setGuidanceFile, setPrompt]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setTemplatesOpen(false);
    };
    const onPointer = (e) => {
      if (composerRef.current?.contains(e.target)) return;
      if (menusRef.current?.contains(e.target)) return;
      setTemplatesOpen(false);
      setComposerActive(false);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onPointer);
    };
  }, []);

  const clearGuidanceImage = () => {
    if (guidancePreviewRef.current) {
      URL.revokeObjectURL(guidancePreviewRef.current);
    }
    setGuidancePreview(null);
    setGuidanceFile(null);
  };

  const handleGuidanceFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (guidancePreviewRef.current) {
        URL.revokeObjectURL(guidancePreviewRef.current);
      }
      setGuidancePreview(URL.createObjectURL(file));
      setGuidanceFile(file);
    }
    e.target.value = "";
  };

  const selectTemplate = (t) => {
    setPrompt(t.prompt.slice(0, PROMPT_MAX_LENGTH));
    setTemplatesOpen(false);
  };

  const menus =
    typeof document !== "undefined"
      ? createPortal(
          <div ref={menusRef} className="abitp-composer-menus">
            {templatesOpen && templatesStyle ? (
              <div
                className="abitp-composer__templates"
                style={templatesStyle}
              >
                <div className="abitp-composer__templates-head">
                  <p className="abitp-composer__templates-title">Templates</p>
                  <p className="abitp-composer__templates-desc">
                    Pick a scene to fill your prompt instantly.
                    You can edit the text anytime after selecting.
                  </p>
                </div>
                <div
                  className="abitp-composer__templates-list"
                  role="listbox"
                  aria-label="Templates"
                >
                  {AI_BACKGROUND_TEMPLATES.map((t) => {
                    const selected = activeTemplate?.id === t.id;
                    return (
                      <button
                        key={t.id}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`abitp-composer__template${selected ? " is-selected" : ""}`}
                        onClick={() => selectTemplate(t)}
                      >
                        <span className="abitp-composer__template-label">
                          {t.label}
                        </span>
                        <span className="abitp-composer__template-prompt">
                          {t.prompt}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
          </div>,
          document.body
        )
      : null;

  return (
    <div className="aftp-section">
      <label className="aftp-label" htmlFor="aftp-bg-prompt">
        Prompt{" "}
        <span className="aftp-optional">(Describe your background )</span>
      </label>

      <div
        ref={composerRef}
        className={`abitp-composer${controlsDisabled ? " is-disabled" : ""}${composerActive || templatesOpen ? " is-active" : ""}`}
        onMouseDown={() => {
          if (!controlsDisabled) setComposerActive(true);
        }}
        onFocusCapture={() => {
          if (!controlsDisabled) setComposerActive(true);
        }}
        onBlurCapture={(e) => {
          const next = e.relatedTarget;
          if (composerRef.current?.contains(next)) return;
          if (menusRef.current?.contains(next)) return;
          if (templatesOpen) return;
          setComposerActive(false);
        }}
      >
        <div className="abitp-composer__body">
          <input
            ref={guidanceInputRef}
            type="file"
            accept={ACCEPT}
            onChange={handleGuidanceFileChange}
            className="abitp-composer__file"
            disabled={controlsDisabled}
          />
          {guidancePreview || guidanceFile ? (
            <div className="abitp-composer__ref is-filled">
              <img src={guidancePreview} alt="Reference" />
              <button
                type="button"
                className="abitp-composer__ref-clear"
                onClick={() => {
                  clearGuidanceImage();
                }}
                disabled={controlsDisabled}
                aria-label="Remove reference image"
              >
                ×
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="abitp-composer__ref"
              onClick={() => guidanceInputRef.current?.click()}
              disabled={controlsDisabled}
              title="Upload reference image"
              aria-label="Upload reference image"
            >
              <PlusIcon />
            </button>
          )}

          <textarea
            id="aftp-bg-prompt"
            className="abitp-composer__input"
            rows={6}
            value={prompt}
            maxLength={PROMPT_MAX_LENGTH}
            onChange={(e) => {
              setPrompt(e.target.value.slice(0, PROMPT_MAX_LENGTH));
            }}
            placeholder="Describe your background…"
            disabled={controlsDisabled}
          />
        </div>

        <div className="abitp-composer__toolbar">
          <div className="abitp-composer__toolbar-left">
            <button
              ref={templatesChipRef}
              type="button"
              className={`abitp-composer__chip${templatesOpen ? " is-open" : ""}${activeTemplate ? " is-active" : ""}`}
              disabled={controlsDisabled}
              aria-expanded={templatesOpen}
              aria-haspopup="listbox"
              onClick={() => {
                setTemplatesOpen((o) => !o);
              }}
            >
              {activeTemplate ? activeTemplate.label : "Templates"}
              <ChevronIcon />
            </button>
          </div>

          <span
            className={`abitp-composer__count${prompt.length >= PROMPT_MAX_LENGTH ? " is-max" : ""}`}
            aria-live="polite"
          >
            {prompt.length}/{PROMPT_MAX_LENGTH}
          </span>
        </div>
      </div>

      {menus}
    </div>
  );
}

/**
 * AI Tools → AI Background Image prep.
 * Source upload stays in AiFeatureToolPrep; other fields lock until an image is loaded.
 */
const BackgroundImageToolPrep = (props) => {
  const [prompt, setPrompt] = useState("");
  const [guidanceFile, setGuidanceFile] = useState(null);

  const handleGenerate = useCallback(
    async ({ token, file, image_url }) =>
      aiBackgroundSingle(token, file, {
        background_prompt: prompt.trim() || undefined,
        output_format: DEFAULT_OUTPUT_FORMAT,
        background_guidance_image: guidanceFile || undefined,
        ...(image_url ? { image_url } : {}),
      }),
    [prompt, guidanceFile]
  );

  const isGenerateReady = useCallback(
    () => Boolean(prompt.trim() || guidanceFile),
    [prompt, guidanceFile]
  );

  return (
    <AiFeatureToolPrep
      {...props}
      toolKey="ai_background_image"
      title="AI Background Image"
      aka="AI Background Generator"
      bio="Replace or generate a new background behind your subject."
      howToSteps={[
        "Upload a subject photo",
        "Describe the background (or pick a template)",
        "Optional: attach a reference image",
        "Tap Generate when ready",
      ]}
      howToTip="Remove Background first for cleaner subject edges against the new scene."
      emptyPreviewLabel="Upload an image to preview background"
      loadingLabel="Generating background…"
      emptyFooterHint="Upload an image, describe a background, then generate."
      removeBgBio="Recommended — works best from a clean cutout of your subject."
      onGenerate={handleGenerate}
      isGenerateReady={isGenerateReady}
      generateFileName="background-input.png"
      doneLabelAddon={
        <span
          className="abitp-done-credits"
          title={`${GENERATION_CREDITS} credits per generation`}
        >
          <CreditsIcon />
          {GENERATION_CREDITS}
        </span>
      }
      renderControls={(ctx) => (
        <BackgroundImageControls
          {...ctx}
          prompt={prompt}
          setPrompt={setPrompt}
          guidanceFile={guidanceFile}
          setGuidanceFile={setGuidanceFile}
        />
      )}
    />
  );
};

export default BackgroundImageToolPrep;
